import {
  extractDateRangesFromDateArray,
  formatDate,
} from '@/lib/es-bill-shared-js/misc/timeTools';

export function computeCutoffDate(offsetDays) {
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - offsetDays);
  return cutoff;
}

export function normalizeToLocalMidnight(dateValue) {
  const d = new Date(dateValue);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function filterBillingMasterData(allBillruns, offsetDays) {
  const cutoff = computeCutoffDate(offsetDays);
  return allBillruns.filter((br) => {
    if (br.billingRunStatus !== 'CREATED') {
      return false;
    }
    const periodTo = normalizeToLocalMidnight(br.billingPeriodTo);
    return periodTo.getTime() <= cutoff.getTime();
  });
}

/**
 * Format BillingMasterData comments (array of Comment objects) for display.
 * Only the `comment` property is used; `date` and `author` are ignored.
 *
 * @param {unknown} comments
 * @returns {string|null} Newline-joined comment texts, or null when nothing to show
 */
export function formatBillingMasterDataComments(comments) {
  if (!Array.isArray(comments) || comments.length === 0) {
    return null;
  }

  const texts = comments
    .map((entry) => (typeof entry?.comment === 'string' ? entry.comment.trim() : ''))
    .filter((text) => text.length > 0);

  if (texts.length === 0) {
    return null;
  }

  return texts.join('\n');
}

/**
 * @param {string[]} selectedDsoEcns
 * @param {string[]} selectedCommunityEcids
 * @returns {{ dsoFilter: Set<string>, communityFilter: Set<string> }}
 */
export function toFilterSets(selectedDsoEcns, selectedCommunityEcids) {
  return {
    dsoFilter: new Set(selectedDsoEcns),
    communityFilter: new Set(selectedCommunityEcids),
  };
}

/**
 * Whether a row or meter point passes the view's DSO and community filters.
 * Empty filter sets mean no filtering on that dimension.
 *
 * @param {{ ecid: string, ecnDso: string }} target
 * @param {Set<string>} dsoFilter
 * @param {Set<string>} communityFilter
 * @returns {boolean}
 */
export function passesViewFilters({ ecid, ecnDso }, dsoFilter, communityFilter) {
  if (communityFilter.size > 0 && !communityFilter.has(ecid)) {
    return false;
  }
  if (dsoFilter.size > 0 && !dsoFilter.has(ecnDso)) {
    return false;
  }
  return true;
}

/**
 * Filter detail rows by DSO ECN and community ECID. Caller applies applyGroupDisplays.
 *
 * @param {object[]} detailRows
 * @param {string[]} selectedDsoEcns
 * @param {string[]} selectedCommunityEcids
 * @returns {object[]}
 */
export function filterDetailRows(detailRows, selectedDsoEcns, selectedCommunityEcids) {
  const { dsoFilter, communityFilter } = toFilterSets(selectedDsoEcns, selectedCommunityEcids);
  return detailRows.filter((row) =>
    passesViewFilters({ ecid: row.ecid, ecnDso: row.ecnDso }, dsoFilter, communityFilter)
  );
}

/**
 * Build DSO and community autocomplete options from detail rows.
 *
 * @param {object[]} detailRows
 * @returns {{ dsoOptions: object[], communityOptions: object[] }}
 */
export function buildDetailRowFilterOptions(detailRows) {
  const dsoSeen = new Map();
  const communitySeen = new Map();

  for (const row of detailRows) {
    if (!dsoSeen.has(row.ecnDso)) {
      dsoSeen.set(row.ecnDso, {
        title: row.dsoDisplayFull,
        value: row.ecnDso,
      });
    }
    if (!communitySeen.has(row.ecid)) {
      communitySeen.set(row.ecid, {
        title: row.communityDisplayFull,
        value: row.ecid,
        ecn: row.ecn,
      });
    }
  }

  const dsoOptions = [...dsoSeen.values()].sort((a, b) =>
    String(a.value).localeCompare(String(b.value), undefined, { numeric: true })
  );
  const communityOptions = [...communitySeen.values()].sort((a, b) =>
    String(a.ecn ?? '').localeCompare(String(b.ecn ?? ''), undefined, { numeric: true })
  );

  return { dsoOptions, communityOptions };
}

/**
 * Format a [start, end] date pair for display (dd.MM.yyyy).
 *
 * @param {[Date, Date]} range
 * @returns {string}
 */
function formatDateRangePair(range) {
  const [start, end] = range;
  const startStr = formatDate(start);
  if (!startStr) {
    return '';
  }
  if (start.getTime() === end.getTime()) {
    return startStr;
  }
  return `${startStr} - ${formatDate(end)}`;
}

/**
 * Dedupe issue dates, merge consecutive days into ranges, format as dd.MM.yyyy strings.
 *
 * @param {Date[]} dates
 * @returns {string[]}
 */
export function formatDateRangesForDisplay(dates) {
  if (dates.length === 0) {
    return [];
  }
  const unique = [...new Map(dates.map((d) => [d.getTime(), d])).values()];
  return extractDateRangesFromDateArray(unique).map(formatDateRangePair);
}
